"use client"
import {
  classNames,
  FlexCenter,
  inputVariants,
  QuantityInput,
  Separator,
  TextBody,
  Tooltip,
  UnstyledButton,
} from "@opensea/ui-kit"
import { Add, Remove } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import { useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { getOfferPricePrecision } from "../../../../../lib/zod/schema"
import { MINIMUM_LISTING_PRICE, OFFER_MAX_DECIMALS } from "../../../constants"
import type { ListingFlowFormValues } from "../../types"

export const IncrementAllPricesInput = () => {
  const t = useTranslations("wallet.IncrementAllPricesInput")
  const [expanded, setExpanded] = useState(false)
  const { control, setValue } = useFormContext<ListingFlowFormValues>()
  const listings = useWatch({ control, name: "listings" })
  const firstListingFloorPrice =
    listings.length > 0 ? listings[0].floorPrice : null
  const decimalPrecision = getOfferPricePrecision(
    firstListingFloorPrice || OFFER_MAX_DECIMALS,
  )
  // Use offer precision as a proxy for intervals 0.01, 0.001, 0.0001
  const defaultIncrementValue = Number(
    (10 ** -decimalPrecision).toFixed(decimalPrecision),
  )
  const [incrementValue, setIncrementValue] = useState<number | null>(
    defaultIncrementValue,
  )

  const handleDecrement = () => {
    if (!incrementValue) {
      return
    }

    listings.forEach((listing, index) => {
      setValue(
        `listings.${index}.price`,
        Math.max(
          bn(listing.price || 0)
            .minus(incrementValue)
            .toNumber(),
          MINIMUM_LISTING_PRICE,
        ),
        { shouldDirty: true },
      )
    })
  }

  const handleIncrement = () => {
    if (!incrementValue) {
      return
    }

    listings.forEach((listing, index) => {
      setValue(
        `listings.${index}.price`,
        bn(listing.price || 0)
          .plus(incrementValue)
          .toNumber(),
        { shouldDirty: true },
      )
    })
  }

  if (!expanded) {
    return (
      <FlexCenter
        className={classNames(inputVariants(), "h-8 w-fit gap-3 px-3 py-1.5")}
      >
        <Tooltip
          content={<TextBody size="xs">{t("decrementAllPrices")}</TextBody>}
        >
          <UnstyledButton onClick={() => setExpanded(true)}>
            <Remove size={20} />
          </UnstyledButton>
        </Tooltip>
        <Separator className="h-5" orientation="vertical" />
        <Tooltip
          content={<TextBody size="xs">{t("incrementAllPrices")}</TextBody>}
        >
          <UnstyledButton onClick={() => setExpanded(true)}>
            <Add size={20} />
          </UnstyledButton>
        </Tooltip>
      </FlexCenter>
    )
  }

  return (
    <QuantityInput
      onValueChange={(value, source) => {
        if (source === "input") {
          setIncrementValue(value)
        } else if (source === "increment") {
          handleIncrement()
        } else {
          handleDecrement()
        }
      }}
      placeholder="0.0"
      size="sm"
      value={incrementValue}
    />
  )
}
